import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { US_STATES } from "@/lib/geo";

type Tpl = { key: string; type: string; jurisdiction: string; description: string };

export const countyLabel = (raw: string) => {
  const names = raw.split(/,|;|\band\b|&/i).map((s) => s.trim().replace(/\s+count(y|ies)$/i, "")).filter(Boolean);
  if (!names.length) return "";
  if (names.length === 1) return `${names[0]} County`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} Counties`;
};

const countiesByState = (addresses: any[]) => {
  const byState: Record<string, string[]> = {};
  addresses.filter((a) => (a.country || "United States") === "United States" && a.county && a.state).forEach((a) => {
    const name = US_STATES.find(([c]) => c === a.state)?.[1] || a.state;
    const cty = String(a.county).replace(/\s+county$/i, "").trim();
    byState[name] = Array.from(new Set([...(byState[name] || []), cty]));
  });
  return byState;
};

const missingCounties = (addresses: any[], existing: string[]) => {
  const out: Record<string, string[]> = {};
  Object.entries(countiesByState(addresses)).forEach(([st, cs]) => {
    const prefix = `${st} court - `.toLowerCase();
    const have = existing.map((t) => t.toLowerCase()).filter((t) => t.startsWith(prefix));
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const left = cs.filter((c) => !have.some((t) => new RegExp(`\\b${esc(c.toLowerCase())}\\b`).test(t.slice(prefix.length))));
    if (left.length) out[st] = left;
  });
  return out;
};

function CountySuggestions({ addresses, existing, state, county, onPick, onAddAll, busy }: { addresses: any[]; existing: string[]; state: string; county: string; onPick: (state: string, county: string) => void; onAddAll: () => void; busy: boolean }) {
  const byState = countiesByState(addresses);
  const states = Object.keys(byState);
  const todo = Object.keys(missingCounties(addresses, existing)).length;
  if (!states.length) return <p className="text-xs text-[#64748B]" data-testid="county-suggestions-empty">No counties in this applicant's address history.</p>;
  const picked = county.split(/,|;|\band\b|&/i).map((s) => s.trim().replace(/\s+count(y|ies)$/i, "").toLowerCase()).filter(Boolean);
  const toggle = (st: string, c: string) => {
    const cur = st === state ? county.split(/,|;|\band\b|&/i).map((s) => s.trim().replace(/\s+count(y|ies)$/i, "")).filter(Boolean) : [];
    const next = cur.some((x) => x.toLowerCase() === c.toLowerCase()) ? cur.filter((x) => x.toLowerCase() !== c.toLowerCase()) : [...cur, c];
    onPick(st, next.join(", "));
  };
  return (
    <div className="space-y-1.5 rounded-lg bg-[#F1EFEA] p-3" data-testid="county-suggestions">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[#475569]">From address history (click to add or remove)</p>
        <button type="button" disabled={busy || !todo} onClick={onAddAll} className="btn-secondary btn-sm" data-testid="county-add-all-button">
          {busy ? "Adding…" : !todo ? "All counties already have a search" : `Add all (${todo} search${todo === 1 ? "" : "es"}, one per state)`}
        </button>
      </div>
      {states.map((st) => (
        <div key={st} className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-[#1A2536]">{st}:</span>
          {byState[st].map((c) => {
            const on = st === state && picked.includes(c.toLowerCase());
            return (
              <button key={c} type="button" onClick={() => toggle(st, c)} data-testid={`county-suggestion-${st}-${c}`.replace(/\s+/g, "-").toLowerCase()}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${on ? "border-[#1B365D] bg-[#1B365D] text-white" : "border-slate-300 bg-white text-[#1A2536] hover:border-[#1B365D]"}`}>{c}</button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function AddSearchForm({ onAdd, addresses = [], existing = [] }: { onAdd: (body: any) => Promise<void>; addresses?: any[]; existing?: string[] }) {
  const [tpls, setTpls] = useState<Tpl[]>([]);
  const [f, setF] = useState({ template: "custom", type: "", jurisdiction: "", description: "", state: "", county: "" });
  useEffect(() => {
    api.get("/admin/search-templates").then((r) => setTpls(r.data));
  }, []);
  const pick = (key: string) => {
    const t = tpls.find((x) => x.key === key);
    if (!t || key === "custom") return setF({ template: "custom", type: "", jurisdiction: "", description: "", state: "", county: "" });
    setF({ template: key, type: t.type, jurisdiction: t.jurisdiction, description: t.description, state: "", county: "" });
  };
  const needsName = /\{(state_name|country)\}/.test(f.type + f.jurisdiction + f.description);
  const needsCounty = /\{county\}/.test(f.type + f.jurisdiction + f.description);
  const fill = (s: string) => s.replace(/\{(state_name|country)\}/g, f.state).replace(/\{county\}/g, countyLabel(f.county));
  const [busy, setBusy] = useState(false);
  const addAll = async () => {
    const t = tpls.find((x) => x.key === "county_courts");
    if (!t) return;
    setBusy(true);
    try {
      for (const [st, cs] of Object.entries(missingCounties(addresses, existing))) {
        const fl = (s: string) => s.replace(/\{state_name\}/g, st).replace(/\{county\}/g, countyLabel(cs.join(", ")));
        await onAdd({ template: "county_courts", type: fl(t.type), jurisdiction: fl(t.jurisdiction), description: fl(t.description) });
      }
      pick("custom");
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onAdd({ template: f.template, type: fill(f.type), jurisdiction: fill(f.jurisdiction), description: fill(f.description) });
    pick("custom");
  };
  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl border-2 border-dashed border-slate-200 p-4" data-testid="case-add-search-form">
      <p className="text-sm font-semibold text-[#1A2536]">Add a search</p>
      <select className="field-input h-10 text-sm" value={f.template} onChange={(e) => pick(e.target.value)} data-testid="case-add-search-template">
        {tpls.map((t) => <option key={t.key} value={t.key}>{t.key === "custom" ? "Custom search…" : t.type.replace("{state_name}", "[State]").replace("{country}", "[Country]").replace("{county}", "[County or Counties]")}</option>)}
      </select>
      {(needsName || needsCounty) && (
        <div className="flex flex-wrap gap-2">
          {needsName && <input required className="field-input h-10 min-w-0 flex-1 text-sm" placeholder="State or country name (e.g. Maryland)" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="case-add-search-state" />}
          {needsCounty && <input required className="field-input h-10 min-w-0 flex-1 text-sm" placeholder="County or counties (e.g. Cecil, Harford)" value={f.county} onChange={(e) => setF({ ...f, county: e.target.value })} data-testid="case-add-search-county" />}
        </div>
      )}
      {needsCounty && <CountySuggestions addresses={addresses} existing={existing} state={f.state} county={f.county} onPick={(state, county) => setF({ ...f, state, county })} onAddAll={addAll} busy={busy} />}
      <div className="flex flex-wrap gap-2">
        <input required className="field-input h-10 flex-1 text-sm" placeholder="Search title" value={fill(f.type)} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="case-add-search-type" />
        <input className="field-input h-10 w-full text-sm sm:w-48" placeholder="Jurisdiction" value={fill(f.jurisdiction)} onChange={(e) => setF({ ...f, jurisdiction: e.target.value })} data-testid="case-add-search-jurisdiction" />
      </div>
      <textarea className="field-input h-auto py-2 text-sm" rows={3} placeholder="Scope description printed on the report" value={fill(f.description)} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="case-add-search-description" />
      <button className="btn-secondary btn-sm" data-testid="case-add-search-button"><Plus className="h-4 w-4" /> Add search</button>
    </form>
  );
}
